import z from "zod"

/**
 * Coordinates validation: coherence error only on the empty field; range error on filled field if out of range.
 * - Coherence: when only one is filled, add issue on the empty field only.
 * - Range: when a field is filled and out of range, add issue on that field.
 */
export type CoordinatesValue = {
  latitude: number | null
  longitude: number | null
}

const LATITUDE_MIN = -90
const LATITUDE_MAX = 90
const LONGITUDE_MIN = -180
const LONGITUDE_MAX = 180

export const isLatitudeInRange = (value: number): boolean =>
  value >= LATITUDE_MIN && value <= LATITUDE_MAX

export const isLongitudeInRange = (value: number): boolean =>
  value >= LONGITUDE_MIN && value <= LONGITUDE_MAX

/** Single code used so the form sets an error on the path; message is chosen in the hook. */
const COORDINATES_INVALID = "coordinatesInvalid"

function collectCoordinateIssues(data: CoordinatesValue): Array<{
  path: [string, ...string[]]
  message: string
}> {
  const latDefined = data.latitude !== undefined && data.latitude !== null
  const lonDefined = data.longitude !== undefined && data.longitude !== null
  const issues: Array<{ path: [string, ...string[]]; message: string }> = []

  if (!latDefined && !lonDefined) {
    return issues
  }

  // Coherence: only one filled → error on the empty field
  if (!latDefined && lonDefined) {
    issues.push({ path: ["latitude"], message: COORDINATES_INVALID })
  }
  if (latDefined && !lonDefined) {
    issues.push({ path: ["longitude"], message: COORDINATES_INVALID })
  }

  // Range: filled and out of range → error on that field
  if (latDefined && !isLatitudeInRange(data.latitude as number)) {
    issues.push({ path: ["latitude"], message: COORDINATES_INVALID })
  }
  if (lonDefined && !isLongitudeInRange(data.longitude as number)) {
    issues.push({ path: ["longitude"], message: COORDINATES_INVALID })
  }
  return issues
}

export const coordinatesSchema = z
  .object({
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
  })
  .strict()
  .superRefine((data, ctx) => {
    for (const { path, message } of collectCoordinateIssues(data)) {
      ctx.addIssue({
        code: "custom",
        message,
        path,
      })
    }
  })
